import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { submitAssessment, getAssessment } from "../../service/assessment";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Container,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
// import echobuddyLogo from "../../assets/echobuddy.png";
import echobuddyLogo from "../../assets/newEchoLogo.png";

const SCALE_7 = [
  "Strongly Disagree",
  "Moderately Disagree",
  "Slightly Disagree",
  "Neutral",
  "Slightly Agree",
  "Moderately Agree",
  "Strongly Agree"
];

const SCALE_4 = [
  "Almost Never",
  "Sometimes",
  "Often",
  "Almost Always"
];

const questions = [
  // Section A (7-point)
  { id: 'a1', section: 'a', text: 'In most ways, my life is close to my ideal.', scale: 7 },
  { id: 'a2', section: 'a', text: 'The conditions of my life are excellent.', scale: 7 },
  { id: 'a3', section: 'a', text: 'I am satisfied with my life.', scale: 7 },
  { id: 'a4', section: 'a', text: 'So far, I have gotten the important things I want in life.', scale: 7 },
  { id: 'a5', section: 'a', text: 'If I could live my life over, I would change almost nothing.', scale: 7 },

  // Section B
  { id: 'b1', section: 'b', text: 'I get excited about learning new things in my classes.', scale: 4 },
  { id: 'b2', section: 'b', text: 'I enjoy working on assignments.', scale: 4 },
  { id: 'b3', section: 'b', text: 'Learning feels meaningful.', scale: 4 },
  { id: 'b4', section: 'b', text: 'I look forward to attending my classes.', scale: 4 },

  // Section C
  { id: 'c1', section: 'c', text: 'I feel like I belong at my college.', scale: 4 },
  { id: 'c2', section: 'c', text: 'I am treated with respect by peers.', scale: 4 },
  { id: 'c3', section: 'c', text: 'There are people who care about me.', scale: 4 },
  { id: 'c4', section: 'c', text: 'I can truly be myself at college.', scale: 4 },

  // Section D
  { id: 'd1', section: 'd', text: 'My education has a clear purpose.', scale: 4 },
  { id: 'd2', section: 'd', text: 'I feel motivated to achieve my goals.', scale: 4 },
  { id: 'd3', section: 'd', text: 'I understand why I am pursuing my degree.', scale: 4 },
  { id: 'd4', section: 'd', text: 'My studies connect to something meaningful.', scale: 4 },

  // Section E
  { id: 'e1', section: 'e', text: 'I am a capable student.', scale: 4 },
  { id: 'e2', section: 'e', text: 'I do my academic work well.', scale: 4 },
  { id: 'e3', section: 'e', text: 'I can handle academic challenges.', scale: 4 },
  { id: 'e4', section: 'e', text: 'When things get difficult, I keep going.', scale: 4 }
];
const SECTION_META = {
  a: { label: 'Section A — Life Satisfaction', color: '#38BDF8' },
  b: { label: 'Section B — Joy of Learning', color: '#818CF8' },
  c: { label: 'Section C — Campus Connectedness', color: '#2CC795' },
  d: { label: 'Section D — Educational Purpose', color: '#F9BE4A' },
  e: { label: 'Section E — Academic Efficacy', color: '#FB923C' },
  f: { label: 'Section F — Emotional Reflection', color: '#A855F7' }
};

const getSectionColor = (sec) => SECTION_META[sec]?.color;
const getSectionLabel = (sec) => SECTION_META[sec]?.label;


const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: '0 12px 40px rgba(16, 24, 40, 0.08)',
  border: '1px solid #E5E7EB',
  backgroundColor: '#ffffff'
}));

const OptionButton = styled(FormControlLabel)(
  ({ theme, checked }) => ({
    margin: '6px 0',
    padding: '12px 16px',
    borderRadius: 14,
    border: checked ? '2px solid #4a6cf7' : '1px solid #E5E7EB',
    backgroundColor: checked ? 'rgba(74,108,247,0.06)' : '#ffffff',
    transition: 'all 0.2s ease',
    cursor: 'pointer',

    '&:hover': {
      borderColor: '#4a6cf7',
      backgroundColor: 'rgba(74,108,247,0.04)'
    },

    '& .MuiFormControlLabel-label': {
      width: '100%',
      marginLeft: 14
    }
  })
);

const HealthSurvey = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentQuestion, setCurrentQuestion] = useState(-1); // Start with -1 to show welcome screen
  const [answers, setAnswers] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // section f :
  const [reflection, setReflection] = useState({
    f1: '',
    f2: '',
    f3: ''
  });

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        const data = await getAssessment(4);

        if (data?.answers) {
          setAnswers(data.answers);
          setReflection(data.description || {});


          setShowWelcome(false);
          setCurrentQuestion(0);
        }

      } catch (err) {
        console.log("No previous assessment found");
      }
    };

    fetchAssessment();
  }, []);


  const handleOptionSelect = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: Number(value)
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };


  const calculateScores = () => {
    const sum = (ids) =>
      ids.reduce((total, id) => total + (answers[id] || 0), 0);

    const score_a = sum(['a1', 'a2', 'a3', 'a4', 'a5']);
    const score_b = sum(['b1', 'b2', 'b3', 'b4']);
    const score_c = sum(['c1', 'c2', 'c3', 'c4']);
    const score_d = sum(['d1', 'd2', 'd3', 'd4']);
    const score_e = sum(['e1', 'e2', 'e3', 'e4']);

    const overall_index = score_b + score_c + score_d + score_e;

    return { score_a, score_b, score_c, score_d, score_e, overall_index };
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      alert("Please answer all questions before submitting.");
      return;
    }
    try {
      setLoading(true);

      const payload = {
        userId: 4, // TEMP for testing (later take from auth)
        answers: answers,
        description: reflection
      };

      const response = await submitAssessment(payload);

      setResult(response);
      setIsSubmitted(true);

    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Show welcome screen
  if (showWelcome) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9ff' }}>
        <Box
          sx={{
            width: '100%',
            px: { xs: 2, sm: 4, md: 6 },
            py: 1,
            display: 'flex',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            borderBottom: '1px solid #E5E7EB',
            position: 'relative',
            rowGap: 0.5
          }}
        >
          {/* LEFT - logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, order: 1 }}>
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img
                src={echobuddyLogo}
                alt="EchoBuddy"
                style={{
                  height: 45,
                  width: 45,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  marginTop: '0.1rem',
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: '#7843f3',
                    lineHeight: 1.1,
                    mb: 0.2,
                  }}
                >
                  echoShine
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7843f3',
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    display: 'block',
                    lineHeight: 1.2,
                  }}
                >
                  Connect. Heal. Rise.
                </Typography>
              </Box>
            </Link>
          </Box>

          {/* CENTER - title */}
          <Typography
            sx={{
              order: { xs: 3, md: 2 },
              width: { xs: '100%', md: 'auto' },
              textAlign: 'center',
              position: { xs: 'static', md: 'absolute' },
              left: { md: '50%' },
              transform: { xs: 'none', md: 'translateX(-50%)' },
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
              fontWeight: 600,
              pb: { xs: 0.5, md: 0 },
              background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Subjective Well-Being Self-Assessment!!
          </Typography>

          {/* RIGHT - back */}
          <Box sx={{ order: { xs: 2, md: 3 } }}>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                color: '#3B3FA0',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '15px',
                padding: 0,
                minWidth: 'auto'
              }}
            >
              ‹ Back
            </Button>
          </Box>
        </Box>
        <Container maxWidth="md" sx={{ mt: 4 }}>

          <StyledCard>
            <CardContent sx={{ p: isMobile ? 3 : 5, textAlign: 'left', }}>
              <Typography variant="body1" paragraph sx={{ mb: 2.5, lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B' }}>
                Welcome to the <strong>echoShine Student Subjective Well-Being (echo SSWB) Self-Assessment</strong>.
                This assessment is tailor-made for college and university students, benchmarked against internationally validated scales — the <em>Student Subjective Wellbeing Questionnaire</em> (SSWQ, Renshaw 2020) and the <em>Satisfaction with Life Scale</em> (SWLS, Diener et al. 1985).
                It helps you reflect on how you are truly doing — academically, socially, and emotionally.
              </Typography>

              <Typography variant="body1" paragraph sx={{ mb: 2.5, lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B' }}>
                There are <strong>24 questions</strong> covering five dimensions of your wellbeing: Life Satisfaction, Joy of Learning, Campus Connectedness, Educational Purpose, and Academic Efficacy — followed by a short optional reflection.
                It would take approximately <strong>5 – 8 minutes</strong> to complete. All scored questions are required for a complete assessment.
              </Typography>

              <Typography variant="body1" paragraph sx={{ mb: 2.5, lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B' }}>
                There are <strong>no right or wrong answers</strong>. Choose the option that most closely reflects how you have been feeling over the <strong>past month</strong>. There is no time limit, but try to go with your natural, honest response rather than overthinking.
              </Typography>

              <Typography variant="body1" paragraph sx={{ mb: 4, lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B' }}>
                Your results are for your own <strong>self-discovery and wellbeing</strong>. They are confidential and available only to you. Use them to understand yourself better and, if needed, as a starting point to seek support.
              </Typography>

              <Box sx={{ textAlign: 'center', mt: 5 }}>
                <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, color: '#2CC795', mb: 3.5 }}>
                  Good luck and start your assessment
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setShowWelcome(false);
                    setCurrentQuestion(0);
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, rgb(44, 199, 149), rgb(59, 63, 160))',
                    color: 'white',
                    px: 7.5,
                    py: 2.25,
                    borderRadius: 3.5,
                    textTransform: 'none',
                    fontSize: '17px',
                    fontWeight: 700,
                    boxShadow: 'rgba(44, 199, 149, 0.35) 0px 6px 24px',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 32px rgba(44,199,149,0.4)'
                    },
                    transition: 'transform 0.18s, box-shadow 0.18s'
                  }}
                >
                  Start Assessment
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Container>
      </Box >
    );
  }

  // Show completion screen
  if (isSubmitted) {
    const scores = result?.score_a
      ? {
        score_a: result.score_a,
        score_b: result.score_b,
        score_c: result.score_c,
        score_d: result.score_d,
        score_e: result.score_e,
        overall_index: result.overallIndex
      }
      : calculateScores();
    const overallMax = 64; // B–E = 16 each
    const overallScore = scores.overall_index;

    const interpretLifeSatisfaction = (score) => {
      if (score >= 31) return "Extremely Satisfied";
      if (score >= 26) return "Satisfied";
      if (score >= 20) return "Neutral";
      if (score >= 15) return "Dissatisfied";
      return "Extremely Dissatisfied";
    };

    const interpret4Scale = (score) => {
      if (score >= 14) return "Almost Always";
      if (score >= 10) return "Often";
      if (score >= 6) return "Sometimes";
      return "Almost Never";
    };

    const interpretOverall = (score) => {
      if (score >= 52) return "Thriving 🌟";
      if (score >= 40) return "Doing Well";
      if (score >= 28) return "Moderate";
      return "Needs Attention";
    };

    return (
      <Box sx={{ bgcolor: '#EEF1F8', minHeight: '100vh', py: 6 }}>
        <Container maxWidth="md">

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Your Wellbeing Report
            </Typography>
            <Typography sx={{ color: '#7B8DB0', mt: 1 }}>
              Here's a snapshot of where you are today.
            </Typography>

            <Box sx={{ mt: 1.5 }}>
              <Button
                component={Link}
                to="/about-report"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#3B3FA0',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: '20px',
                  border: '1px solid #DDE3F5',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(59,63,160,0.05)',
                    borderColor: '#3B3FA0'
                  }
                }}
              >
                ⓘ About this report &amp; sources →
              </Button>
            </Box>
          </Box>

          {/* Overall Banner */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #3B3FA0, #5B61C5)',
              borderRadius: '20px',
              p: 4,
              textAlign: 'center',
              color: 'white',
              mb: 4
            }}
          >
            <Typography sx={{ fontSize: 16, letterSpacing: 1, opacity: 0.8 }}>
              Overall Wellbeing Index (Sections B – E)
            </Typography>

            <Typography sx={{ fontSize: 64, fontWeight: 700 }}>
              {overallScore}
            </Typography>

            <Typography sx={{ opacity: 0.7 }}>
              out of {overallMax}
            </Typography>

            <Box
              sx={{
                mt: 2,
                display: 'inline-block',
                px: 3,
                py: 1,
                borderRadius: '30px',
                background: 'rgba(255,255,255,0.2)',
                fontWeight: 600
              }}
            >
              {interpretOverall(overallScore)}
            </Box>
          </Box>

          {/* Results Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
              mb: 4
            }}
          >
            {[
              {
                label: 'Life Satisfaction',
                score: scores.score_a,
                max: 35,
                color: '#38BDF8',
                interp: interpretLifeSatisfaction(scores.score_a)
              },
              {
                label: 'Joy of Learning',
                score: scores.score_b,
                max: 16,
                color: '#818CF8',
                interp: interpret4Scale(scores.score_b)
              },
              {
                label: 'Campus Connectedness',
                score: scores.score_c,
                max: 16,
                color: '#2CC795',
                interp: interpret4Scale(scores.score_c)
              },
              {
                label: 'Educational Purpose',
                score: scores.score_d,
                max: 16,
                color: '#F9BE4A',
                interp: interpret4Scale(scores.score_d)
              },
              {
                label: 'Academic Efficacy',
                score: scores.score_e,
                max: 16,
                color: '#FB923C',
                interp: interpret4Scale(scores.score_e)
              }
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  background: 'white',
                  borderRadius: '16px',
                  p: 3,
                  textAlign: 'center',
                  boxShadow: '0 2px 12px rgba(59,63,160,0.07)'
                }}
              >
                <Typography sx={{ fontSize: 12, color: '#7B8DB0', mb: 1 }}>
                  {item.label}
                </Typography>

                <Typography sx={{ fontSize: 36, fontWeight: 700, color: item.color }}>
                  {item.score}
                </Typography>

                <Typography sx={{ fontSize: 13, color: '#A0AECB' }}>
                  / {item.max}
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    px: 2,
                    py: 0.5,
                    borderRadius: '20px',
                    display: 'inline-block',
                    background: '#2CC79518',
                    color: '#2CC795'
                  }}
                >
                  {item.interp}
                </Typography>
              </Box>

            ))}
          </Box>

          {/* wellbeing profile  */}
          <Box
            sx={{
              background: "white",
              borderRadius: "20px",
              p: 1.5,
              boxShadow: "0 2px 16px rgba(59,63,160,0.07)",
              mb: 1.5
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Wellbeing Profile
            </Typography>

            {[
              {
                label: "Life Satisfaction",
                score: scores.score_a,
                max: 35,
                color: "#38BDF8"
              },
              {
                label: "Joy of Learning",
                score: scores.score_b,
                max: 16,
                color: "#818CF8"
              },
              {
                label: "Campus Connectedness",
                score: scores.score_c,
                max: 16,
                color: "#2CC795"
              },
              {
                label: "Educational Purpose",
                score: scores.score_d,
                max: 16,
                color: "#F9BE4A"
              },
              {
                label: "Academic Efficacy",
                score: scores.score_e,
                max: 16,
                color: "#FB923C"
              }
            ].map((item, i) => {
              const percentage = (item.score / item.max) * 100;

              return (
                <Box key={i} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>

                    <Typography sx={{ color: item.color, fontWeight: 600 }}>
                      {item.score}/{item.max}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      height: 10,
                      borderRadius: 10,
                      backgroundColor: "#E5E7EB",
                      overflow: "hidden"
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${percentage}%`,
                        backgroundColor: item.color,
                        transition: "width 0.6s ease"
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={() => window.print()}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                border: '1.5px solid #DDE3F5',
                background: 'white'
              }}
            >
              🖨 Print / Save PDF
            </Button>

            <Button
              onClick={() => {
                setIsSubmitted(false);
                setCurrentQuestion(0);
                setAnswers({});
                setReflection({
                  f1: '',
                  f2: '',
                  f3: ''
                });
              }}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: '#3B3FA0',
                color: 'white'
              }}
            >
              ↺ Retake Assessment
            </Button>
          </Box>

        </Container>
      </Box>
    );
  }

  // Calculate variables for survey flow
  const isReflectionSection = currentQuestion === questions.length;

  const currentQ = !isReflectionSection
    ? questions[currentQuestion]
    : null;

  const totalSteps = questions.length + 1;
  const progress = ((currentQuestion + 1) / totalSteps) * 100;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9ff', pt: 4, position: 'relative' }}>
      {/* Logo positioned at top-left */}

      <Box sx={{
        position: 'absolute',
        top: { xs: 18, sm: 18, md: 24 },
        left: { xs: 16, sm: 24, md: 32 },
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.75
      }}>
        <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Logo */}
          <img
            src={echobuddyLogo}
            alt="Echo"
            style={{
              height: 45,
              width: 45,
              borderRadius: '50%',
              objectFit: 'cover',
              marginTop: "0.1rem"
            }}
          />
          {/* Text */}
          <Box>
            <Typography sx={{
              fontSize: '1.4rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              color: "#7843f3",
              lineHeight: 1.1,
              mb: 0.2
            }}>
              echoShine
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#7843f3",
                fontSize: '0.65rem',
                fontWeight: 500,
                letterSpacing: '0.3px',
                display: 'block',
                lineHeight: 1.2
              }}
            >
              Connect. Heal. Rise.
            </Typography>
          </Box>
        </Link>
      </Box>

      <Container maxWidth={false} sx={{ maxWidth: 760, mx: 'auto' }}>
        {/* Header with Back Button */}


        {/* Progress Bar */}
        <Box sx={{ mb: 3, px: isMobile ? 2 : 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#4a6cf7', fontWeight: 500 }}>
              Question {currentQuestion + 1} of {totalSteps}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7c93' }}>
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e9ecef',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(90deg, #4a6cf7 0%, #2541b2 100%)'
              }
            }}
          />
        </Box>

        {/* Question Card */}
        {!isReflectionSection ? (


          // A–E QUESTION UI

          <StyledCard>
            <CardContent sx={{ px: isMobile ? 3 : 6.5, py: isMobile ? 4 : 5.5 }}>

              {/* Section Label */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                mt: -2,
                color: getSectionColor(currentQ.section)
              }}>
                <Box sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: getSectionColor(currentQ.section)
                }} />
                <Typography sx={{ fontWeight: 600 }}>
                  {getSectionLabel(currentQ.section)}
                </Typography>
              </Box>

              {/* Question */}
              <Typography sx={{
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                fontWeight: 500,
                color: '#2C2F6B',
                mb: 1.5
              }}>
                {currentQ.text}
              </Typography>

              {/* Options */}
              <FormControl sx={{ width: '100%' }}>
                <RadioGroup
                  value={answers[currentQ.id] ?? ''}
                  onChange={(e) =>
                    handleOptionSelect(currentQ.id, Number(e.target.value))
                  }
                >
                  {(currentQ.scale === 7 ? SCALE_7 : SCALE_4).map(
                    (option, index) => {
                      const numericValue = index + 1;

                      return (
                        <OptionButton
                          key={index}
                          value={numericValue}
                          control={<Radio />}
                          label={option}
                          checked={answers[currentQ.id] === numericValue}
                        />
                      );
                    }
                  )}
                </RadioGroup>
              </FormControl>

              {/* Navigation Bar */}
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                {/* Back Button */}
                <Button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  sx={{
                    px: 5,
                    py: 1.6,
                    borderRadius: '14px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    backgroundColor: '#E5E7EB',
                    color: '#6B7280',
                    boxShadow: 'none',
                    visibility: currentQuestion === 0 ? 'hidden' : 'visible',
                    '&:hover': {
                      backgroundColor: '#D1D5DB',
                      boxShadow: 'none'
                    }
                  }}
                >
                  ← Back
                </Button>

                {/* Next Button */}
                <Button
                  onClick={handleNext}
                  disabled={!answers[currentQ.id]}
                  sx={{
                    px: 6,
                    py: 1.6,
                    borderRadius: '14px',
                    backgroundColor: '#3F3E9E',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#ffffff',
                    boxShadow: '0 4px 14px rgba(76,75,183,0.35)',

                    // Prevent hover color change
                    '&:hover': {
                      backgroundColor: '#3F3E9E',
                      boxShadow: '0 4px 14px rgba(76,75,183,0.35)'
                    },

                    // Prevent active (click) color change
                    '&:active': {
                      backgroundColor: '#3F3E9E'
                    },

                    // Prevent focus highlight change
                    '&.Mui-focusVisible': {
                      backgroundColor: '#3F3E9E'
                    },

                    // Disabled state styling
                    '&.Mui-disabled': {
                      backgroundColor: '#C7C9F4',
                      color: '#ffffff'
                    }
                  }}
                >
                  Next →
                </Button>
              </Box>

            </CardContent>
          </StyledCard>

        ) : (
          // SECTION F UI
          <Box>

            {/* Section Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 1,
                color: SECTION_META.f.color
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: SECTION_META.f.color
                }}
              />
              <Typography sx={{ fontWeight: 600 }}>
                {SECTION_META.f.label}
              </Typography>
            </Box>

            {/* Question 1 */}
            <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}>
              How would you describe your overall emotional state this month?
            </Typography>

            <Box sx={{
              background: 'white',
              borderRadius: '20px',
              padding: '30px 36px',
              boxShadow: '0 2px 16px rgba(59,63,160,0.07)',
              mb: 4
            }}>
              <textarea
                value={reflection.f1}
                onChange={(e) => setReflection({ ...reflection, f1: e.target.value })}
                placeholder="Share how you've been feeling in one or two sentences…"
                rows={3}
                style={{
                  width: '100%',
                  border: '1.5px solid #DDE3F5',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  fontSize: '15px',
                  background: '#FAFBFF'
                }}
              />
            </Box>

            {/* Question 2 */}
            <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}>
              What is one thing about your college experience that positively supports your wellbeing?
            </Typography>

            <Box sx={{
              background: 'white',
              borderRadius: '20px',
              padding: '30px 36px',
              boxShadow: '0 2px 16px rgba(59,63,160,0.07)',
              mb: 4
            }}>
              <textarea
                value={reflection.f2}
                onChange={(e) => setReflection({ ...reflection, f2: e.target.value })}
                placeholder="Something that helps or energises you…"
                rows={3}
                style={{
                  width: '100%',
                  border: '1.5px solid #DDE3F5',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  fontSize: '15px',
                  background: '#FAFBFF'
                }}
              />
            </Box>

            {/* Question 3 */}
            <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}>
              Is there anything currently affecting your mental or emotional health that you would like support with?
            </Typography>

            <Box sx={{
              background: 'white',
              borderRadius: '20px',
              padding: '30px 36px',
              boxShadow: '0 2px 16px rgba(59,63,160,0.07)',
              mb: 4
            }}>
              <textarea
                value={reflection.f3}
                onChange={(e) => setReflection({ ...reflection, f3: e.target.value })}
                placeholder="You can share as much or as little as you like…"
                rows={3}
                style={{
                  width: '100%',
                  border: '1.5px solid #DDE3F5',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  fontSize: '15px',
                  background: '#FAFBFF'
                }}
              /></Box>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, mb: 4 }}>
              <Button onClick={handlePrevious}>← Back</Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
                sx={{
                  px: '32px',
                  py: '14px',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  backgroundColor: '#2CC795',
                  color: '#ffffff',
                  boxShadow: '0 4px 18px rgba(44,199,149,0.3)',

                  transition: 'all 0.18s ease',

                  '&:hover': {
                    backgroundColor: '#22A97D',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 18px rgba(44,199,149,0.3)'
                  },

                  '&:active': {
                    backgroundColor: '#22A97D'
                  }
                }}
              >
                {loading ? 'Submitting...' : 'Submit →'}
              </Button>
            </Box>

          </Box>

        )}
      </Container>
    </Box>
  );
};

export default HealthSurvey;